import { MoreVertical } from 'lucide-react';
import { IconButton, OverflowMenu, type OverflowMenuAction } from '../ui';

// A Files row's "more options" TRIGGER, in whichever form the width calls for.
//
// **Wide → the popover. Narrow → a bottom sheet.** Same callbacks, same 26px kebab; only the
// presentation differs. A popover anchored to a 26px control is a pointer idiom — on a phone it
// opens under the thumb that summoned it, it has no room to name the file it belongs to, and it is not
// what either native app draws.
//
// The split is the `narrowPane` boolean handed down from whoever provides the `files` size container
// (`FilesBrowser` / `FilesFlatList`), NOT a `useNarrow()` of its own. That is deliberate and it is the
// fix for a real gap: this menu used to ask the VIEWPORT at 720 while the row's share glyph was hidden
// by `@container files (max-width: 600px)` — this PANE. A wide window with a docked chat squeezes the
// pane under 600, so the glyph vanished, the viewport still said "wide", the popover opened with no
// grant-access entry, and the action was unreachable. Three decisions, one question, one box.
//
// ONE action differs between the two presentations, and it is decided upstream rather than here: the
// narrow sheet's last primary item is GRANT ACCESS, which the wide row says with its own share
// `IconButton` instead (redesign §7) and which a narrow row renders no glyph to say. `FilesBrowser`
// hands `onGrantAccess` to the menu builder off the SAME `narrowPane` it passes here, so the item and
// the presentation carrying it cannot disagree — see `lib/file-actions.ts`. This component still
// receives whatever `groups` it is given and renders them; it knows nothing about which entries they
// hold.
//
// The SHEET itself is not rendered here: it is hoisted to the list (`FilesTable` / `FilesGrid`), because
// a portal does not escape React's event bubbling and a scrim press inside a row's subtree would bubble
// to the row and open the file underneath the sheet being dismissed. This component only says "open it"
// — see `FileActionsSheet`.
export function FileRowMenu({
  narrowPane,
  groups,
  ariaLabel,
  onOpen,
  onSheet,
}: {
  /** The enclosing `files` container is at or under `FILES_FOLD_BREAKPOINT_PX` — see the note above. */
  narrowPane: boolean;
  groups: OverflowMenuAction[][];
  ariaLabel: string;
  /** BI for "the row menu was opened" — fired by both presentations, so the beacon is width-blind. */
  onOpen?: () => void;
  /** Asks the enclosing list to open ITS sheet for this row. Narrow only. */
  onSheet: () => void;
}) {
  if (!narrowPane) {
    // The Files options popover is a DENSER menu than the app's default Action Menu — 12/16 SemiBold
    // items in a 26px row on a 12·16 card (Figma `15717:140124`). A variant, not a new default: see
    // `OverflowMenu`'s `density`.
    return (
      <OverflowMenu
        groups={groups}
        ariaLabel={ariaLabel}
        triggerSize="tiny"
        triggerIcon={<MoreVertical size={16} aria-hidden />}
        density="compact"
        onOpen={onOpen}
      />
    );
  }

  return (
    <IconButton
      size="tiny"
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      onClick={(e) => {
        // The row is a link; the kebab acts on the row rather than navigating it.
        e.preventDefault();
        e.stopPropagation();
        onOpen?.();
        onSheet();
      }}
    >
      <MoreVertical size={16} aria-hidden />
    </IconButton>
  );
}
